/**
 * Simple Client-Side Router for SPA
 */

class Router {
    constructor() {
        this.routes = {};
        this.currentRoute = null;
        this.beforeEach = null;

        // Listen to hash changes
        window.addEventListener('hashchange', () => this.handleRoute());
        // Note: Initial route is handled by main.js after auth state is known
    }

    /**
     * Register a route
     * @param {string} path - Route path (e.g., '/dashboard', '/admin/students')
     * @param {Function} handler - Route handler function
     * @param {Object} options - Route options (auth, role, etc.)
     */
    register(path, handler, options = {}) {
        this.routes[path] = { handler, options };
        return this;
    }

    /**
     * Navigate to a path
     * @param {string} path - Path to navigate to
     */
    navigate(path) {
        window.location.hash = path;
    }

    /**
     * Get current path from hash
     * @returns {string} Current path
     */
    getCurrentPath() {
        return window.location.hash.slice(1) || '/';
    }

    /**
     * Parse path to extract params
     * @param {string} routePath - Route pattern (e.g., '/flashcards/:id')
     * @param {string} actualPath - Actual path (e.g., '/flashcards/abc123')
     * @returns {Object|null} Params object or null if no match
     */
    matchRoute(routePath, actualPath) {
        const routeParts = routePath.split('/');
        const pathParts = actualPath.split('/');

        if (routeParts.length !== pathParts.length) {
            return null;
        }

        const params = {};

        for (let i = 0; i < routeParts.length; i++) {
            if (routeParts[i].startsWith(':')) {
                // Dynamic parameter
                params[routeParts[i].slice(1)] = pathParts[i];
            } else if (routeParts[i] !== pathParts[i]) {
                // No match
                return null;
            }
        }

        return params;
    }

    /**
     * Find matching route for current path
     * @param {string} path - Current path
     * @returns {Object|null} Matched route info
     */
    findRoute(path) {
        // First try exact match
        if (this.routes[path]) {
            return {
                route: this.routes[path],
                params: {},
                path
            };
        }

        // Try pattern matching
        for (const [routePath, route] of Object.entries(this.routes)) {
            const params = this.matchRoute(routePath, path);
            if (params !== null) {
                return { route, params, path: routePath };
            }
        }

        return null;
    }

    /**
     * Handle current route
     */
    async handleRoute() {
        const path = this.getCurrentPath();
        const matched = this.findRoute(path);

        if (!matched) {
            // 404 - redirect to home or show error
            console.warn(`Route not found: ${path}`);
            this.navigate('/');
            return;
        }

        const { route, params } = matched;

        // Run beforeEach guard if exists
        if (this.beforeEach) {
            const canProceed = await this.beforeEach(route, path, params);
            if (!canProceed) return;
        }

        // Update current route
        this.currentRoute = { path, params, options: route.options };

        // Call route handler
        try {
            await route.handler(params);
        } catch (error) {
            console.error('Route handler error:', error);
        }
    }

    /**
     * Set navigation guard
     * @param {Function} guard - Guard function (route, path, params) => boolean
     */
    setBeforeEach(guard) {
        this.beforeEach = guard;
    }

    /**
     * Get query parameters from URL
     * @returns {Object} Query params
     */
    getQuery() {
        const hash = window.location.hash;
        const queryIndex = hash.indexOf('?');

        if (queryIndex === -1) return {};

        const queryString = hash.slice(queryIndex + 1);
        const params = new URLSearchParams(queryString);

        return Object.fromEntries(params.entries());
    }
}

// Create and export singleton instance
export const router = new Router();

// Helper to create links
export function createLink(path, text, className = '') {
    const a = document.createElement('a');
    a.href = `#${path}`;
    a.textContent = text;
    a.className = className;
    return a;
}

// Helper for programmatic navigation
export function navigateTo(path) {
    router.navigate(path);
}
